class HiddenNav {
	private nav: HTMLElement | null = null
	private trigger: HTMLButtonElement | null = null
	private closeButton: HTMLButtonElement | null = null
	private links: HTMLAnchorElement[] = []
	private lastFocused: HTMLElement | null = null

	init(container: Document | HTMLElement = document): void {
		this.nav = container.querySelector<HTMLElement>('.hg-hidden-nav') || document.querySelector<HTMLElement>('.hg-hidden-nav')
		this.trigger = container.querySelector<HTMLButtonElement>('[data-hg-header-menu]') || document.querySelector<HTMLButtonElement>('[data-hg-header-menu]')
		this.closeButton = this.nav?.querySelector<HTMLButtonElement>('[data-hg-hidden-nav-close]') || null
		this.links = this.nav ? Array.from(this.nav.querySelectorAll<HTMLAnchorElement>('a')) : []

		if (!this.nav || !this.trigger || !this.closeButton) return

		this.trigger.addEventListener('click', this.onTriggerClick)
		this.closeButton.addEventListener('click', this.onCloseClick)
		this.links.forEach((link) => link.addEventListener('click', this.onLinkClick))
		document.addEventListener('keydown', this.onKeydown)
		window.addEventListener('resize', this.onResize, { passive: true })
	}

	update(container: Document | HTMLElement = document): void {
		this.cleanup()
		this.init(container)
	}

	cleanup(): void {
		this.trigger?.removeEventListener('click', this.onTriggerClick)
		this.closeButton?.removeEventListener('click', this.onCloseClick)
		this.links.forEach((link) => link.removeEventListener('click', this.onLinkClick))
		document.removeEventListener('keydown', this.onKeydown)
		window.removeEventListener('resize', this.onResize)
		this.close(false)
	}

	private open(): void {
		if (!this.nav || !this.trigger || this.isOpen()) return

		this.lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
		this.nav.classList.add('is-open')
		this.nav.setAttribute('aria-hidden', 'false')
		this.nav.removeAttribute('inert')
		this.trigger.setAttribute('aria-expanded', 'true')
		this.trigger.setAttribute('aria-label', this.trigger.dataset.closeLabel || 'Cerrar menú')
		document.body.classList.add('has-hidden-nav-open')
		this.closeButton?.focus({ preventScroll: true })
	}

	private close(restoreFocus = true): void {
		if (!this.nav || !this.trigger) return

		this.nav.classList.remove('is-open')
		this.nav.setAttribute('aria-hidden', 'true')
		this.nav.setAttribute('inert', '')
		this.trigger.setAttribute('aria-expanded', 'false')
		this.trigger.setAttribute('aria-label', this.trigger.dataset.openLabel || 'Abrir menú')
		document.body.classList.remove('has-hidden-nav-open')

		if (restoreFocus && this.lastFocused) {
			this.lastFocused.focus({ preventScroll: true })
		}

		this.lastFocused = null
	}

	private isOpen(): boolean {
		return !!this.nav?.classList.contains('is-open')
	}

	private onTriggerClick = (): void => {
		if (this.isOpen()) this.close()
		else this.open()
	}

	private onCloseClick = (): void => {
		this.close()
	}

	private onLinkClick = (): void => {
		this.close(false)
	}

	private onKeydown = (e: KeyboardEvent): void => {
		if (e.key === 'Escape' && this.isOpen()) {
			this.close()
			return
		}

		if (e.key !== 'Tab' || !this.isOpen()) return

		const focusable = this.getFocusableElements()
		if (!focusable.length) return

		const first = focusable[0]
		const last = focusable[focusable.length - 1]

		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault()
			last.focus()
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault()
			first.focus()
		}
	}

	private onResize = (): void => {
		if (window.innerWidth >= 1280 && this.isOpen()) {
			this.close(false)
		}
	}

	private getFocusableElements(): HTMLElement[] {
		if (!this.nav) return []

		return Array.from(this.nav.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'))
	}
}

export default new HiddenNav()
